/**
 * Round 107 (2026-02-14) — Strategist recommendations action bar.
 *
 * Rendered on mission-linked strategist result screens. The
 * strategist is a REVIEW step — it prepares recommendations. The
 * customer then clicks the primary "Create ..." button which
 * routes back to the Dashboard and opens the existing specialized
 * builder for that asset type, with full mission context threaded
 * through so the builder is prefilled.
 *
 * Finalization NEVER happens on the strategist page (see Round
 * 107 spec). It happens after the specialized builder actually
 * creates the requested asset.
 *
 * Buttons:
 *   • Copy Full Asset (strategist recommendations)
 *   • Download (.txt of the recommendations)
 *   • Create <Asset>  — primary; routes to dashboard + opens builder
 *   • Back to Dashboard — explicit exit; no auto-redirect
 */
import React from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Copy, DownloadSimple, Sparkle,
} from '@phosphor-icons/react';
import useDeepLinkParams from '@/hooks/useDeepLinkParams';
import { getBuilderForAssetType } from '@/utils/missionBuilderMap';

const MissionFinalizeActions = ({
  outputs,
  renderAsText,
  filenamePrefix = 'paygbot-asset',
  testIdPrefix = 'mission-actions',
  // Legacy props preserved for backwards-compat with callers that
  // haven't been migrated yet — silently ignored.
  buildMetaPatch, // eslint-disable-line no-unused-vars
  extras,         // eslint-disable-line no-unused-vars
  botRunId,       // eslint-disable-line no-unused-vars
  projectName,    // eslint-disable-line no-unused-vars
}) => {
  const navigate = useNavigate();
  const deepLink = useDeepLinkParams();

  const missionAssetId = deepLink.asset_id || deepLink.mission_id || null;
  const hasMission = Boolean(missionAssetId);
  const missionCtx = deepLink.mission || {};
  // Round 107.1 — the mission wctx is the authoritative source
  // for asset_type. The URL query `asset_type` param is set by
  // DailyMissionCard to the bot-internal focus (e.g. 'post',
  // 'reel'), NOT the mission's canonical asset_type. Trust wctx
  // first, then fall through to the URL param only if wctx is
  // silent.
  const wctxAssetType = missionCtx.asset_type || null;
  const urlAssetType = deepLink.asset_type || null;
  const assetType = (
    (wctxAssetType && getBuilderForAssetType(wctxAssetType) && wctxAssetType)
    || (urlAssetType && getBuilderForAssetType(urlAssetType) && urlAssetType)
    || wctxAssetType
    || urlAssetType
    || null
  );
  const builder = getBuilderForAssetType(assetType);

  if (!hasMission) return null;

  const handleCopyAll = async () => {
    const text = renderAsText ? renderAsText(outputs) : '';
    if (!text) {
      toast.error('Nothing to copy yet.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Strategist recommendations copied.');
    } catch (err) {
      console.error('Clipboard failed:', err);
      toast.error('Could not copy — select the text and copy manually.');
    }
  };

  const handleDownload = () => {
    const text = renderAsText ? renderAsText(outputs) : '';
    if (!text) {
      toast.error('Nothing to download yet.');
      return;
    }
    try {
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filenamePrefix}-${Date.now()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Could not download.');
    }
  };

  const handleContinueToBuilder = () => {
    if (!builder) {
      // Safety fallback — no known builder for this asset type.
      // Send the customer to Ask PayGBot with full context so a
      // human-led path stays available (per spec §3 fallback).
      navigate('/ask-paygbot?wctx=' + encodeURIComponent(new URLSearchParams(window.location.search).get('wctx') || ''));
      return;
    }
    // Thread the mission context through to the dashboard so the
    // specialized builder can prefill from it. `open_builder`
    // matches an entry in DashboardPage.handleWhatsNextAction().
    const params = new URLSearchParams();
    params.set('open_builder', builder.action);
    if (missionAssetId) params.set('mid', missionAssetId);
    if (assetType) params.set('mission_asset_type', assetType);
    const rawWctx = new URLSearchParams(window.location.search).get('wctx');
    if (rawWctx) params.set('wctx', rawWctx);
    // Also carry the strategist's output blob so the builder can
    // ingest the polished recommendations as prefill inputs.
    if (outputs) {
      try {
        const enc = btoa(unescape(encodeURIComponent(JSON.stringify(outputs))));
        params.set('strategist', enc);
      } catch (_) {
        /* non-fatal */
      }
    }
    navigate(`/dashboard?${params.toString()}`);
  };

  const handleBack = () => {
    navigate(deepLink.return_to || '/dashboard');
  };

  const primaryLabel = builder ? builder.label : 'Continue in Ask PayGBot';

  return (
    <div
      className="rounded-xl border-2 border-amber-300 bg-amber-50 p-6 space-y-4"
      data-testid={`${testIdPrefix}-panel`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-1">
          <Sparkle
            size={28}
            weight="fill"
            className="text-amber-600"
          />
        </div>
        <div className="flex-1">
          <h3
            className="text-lg font-semibold text-amber-900"
            data-testid={`${testIdPrefix}-recs-title`}
          >
            Strategist recommendations ready.
          </h3>
          <p className="text-sm text-amber-900/80 mt-1">
            Review the ideas below. When you're ready, continue to
            the builder — it opens prefilled with your approved
            edits and Business Memory.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleCopyAll}
          data-testid={`${testIdPrefix}-copy-all`}
          className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-white border border-slate-200 text-slate-800 font-medium hover:bg-slate-50 transition-colors"
        >
          <Copy size={18} weight="duotone" />
          <span>Copy Recommendations</span>
        </button>

        <button
          type="button"
          onClick={handleDownload}
          data-testid={`${testIdPrefix}-download`}
          className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-white border border-slate-200 text-slate-800 font-medium hover:bg-slate-50 transition-colors"
        >
          <DownloadSimple size={18} weight="duotone" />
          <span>Download (.txt)</span>
        </button>

        <button
          type="button"
          onClick={handleContinueToBuilder}
          data-testid={`${testIdPrefix}-continue-to-builder`}
          className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold transition-colors"
        >
          <span>{primaryLabel}</span>
          <ArrowRight size={18} weight="bold" />
        </button>

        <button
          type="button"
          onClick={handleBack}
          data-testid={`${testIdPrefix}-back-to-dashboard`}
          className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-colors"
        >
          <ArrowLeft size={18} weight="bold" />
          <span>Back to Dashboard</span>
        </button>
      </div>
    </div>
  );
};

export default MissionFinalizeActions;
